import { Box, styled } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { AI, PURPLE } from 'theme/prismDesignTokens';

/**
 * Footnote badge treatment (SCAL-8969, hue per provenance SCAL-9336). Two axes:
 *   - review status: PENDING gets the review purple fill (dismissed badges never
 *     render - filtered upstream);
 *   - provenance: an AI-CREATED citation keeps the AI border and, once settled,
 *     a light AI-tinted fill - so an accepted AI citation stays distinguishable
 *     from an analyst-created one (plain primary blue) at rest. Non-reviewers
 *     never reach the purple/AI branches: CitationValueViewer collapses status
 *     and provenance to their neutral values for them (SCAL-9335).
 */
const badgeFill = (pending: boolean, aiCreated: boolean, active: boolean) => {
  if (pending) {
    return {
      backgroundColor: active ? PURPLE[500] : PURPLE[100],
      color: active ? 'common.white' : PURPLE[600],
    };
  }
  if (aiCreated) {
    return {
      backgroundColor: active ? AI : alpha(AI, 0.12),
      color: active ? 'common.white' : PURPLE[600],
    };
  }
  return {
    backgroundColor: active ? 'primary.main' : 'primary.light',
    color: active ? 'primary.contrastText' : 'primary.dark',
  };
};

export const citationBadgeSx = ({
  pending,
  aiCreated,
  active,
}: {
  pending: boolean;
  aiCreated: boolean;
  active: boolean;
}): SxProps<Theme> => ({
  cursor: 'pointer',
  ...badgeFill(pending, aiCreated, active),
  ...(aiCreated && { border: `1px solid ${AI}` }),
});

export const StyledCitationValueViewerWrapper = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  // Overlaying inset:0 covers the td's own `padding: 0 4px`; restore it here so
  // the value keeps the standard cell inset.
  padding: '2px 4px',
  // The datasheet td forces `box-sizing: content-box` and every descendant
  // inherits it (CssBaseline `box-sizing: inherit`). The AI finding/merge box
  // stamps `& > * { width: 100% }` on this wrapper when both wrap the cell;
  // under content-box that resolved to 100% + 8px of padding, bleeding the
  // overlay 8px past the td and pushing the value text off the standard right
  // inset (live-verified on the OIP row). Border-box pins the overlay to the
  // cell no matter what width an ancestor stamps on it.
  boxSizing: 'border-box',
}));

export const StyledCitationNumber = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '14px',
  width: 'auto',
  height: '18px',
  fontSize: '0.6rem',
  fontWeight: 600,
  padding: '0 2px',
  borderRadius: '2px',
  flexShrink: 0,
}));

export const StyledCitationNumberWrapper = styled(Box)(() => ({
  position: 'absolute',
  // `left` is set inline by CitationValueViewer: 2px from the cell's left edge
  // by default, or just past the Pre-FX currency chip's MEASURED right edge when
  // the cell renders one (cap-table OIP), so the footnote sits to the chip's
  // right instead of overlapping it (SCAL-9062). Inline (not a styled prop)
  // because the measured offset varies per cell/zoom and would otherwise mint an
  // emotion class per pixel value.
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: '2px',
  zIndex: 10,
  pointerEvents: 'auto',
}));

export const StyledCitationValueViewer = styled(Box, {
  shouldForwardProp: prop => prop !== 'reservedBadgeWidth',
})<{ reservedBadgeWidth?: number }>(({ reservedBadgeWidth = 0 }) => ({
  // position: relative so an absolutely-positioned inner control (the date
  // picker Root) anchors to this padded box rather than the cell, letting the
  // reserved gutter actually shift it right (SCAL-8987).
  position: 'relative',
  flex: 1,
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Dropdown and date-picker cells render their own control inside the cell, so
  // the badge cannot overlay them. Reserve room on the left for the badge gutter
  // and shift the control right instead of letting them fight (SCAL-8987).
  ...(reservedBadgeWidth > 0 && { paddingLeft: `${reservedBadgeWidth}px` }),
}));
